import { Configuration, EventType, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';

import { PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';

import { PRE_AUTH_PATH_KEY } from '@@common';
import { ConfigContext } from '@@components';

import { IAuthService } from '../authentication/AuthService';
import Spinner from '../common/components/Spinner';
import { createScopedLogger } from '../common/logger';
import AuthContext, { Auth } from './AuthContext';
import { Config } from './ConfigContext';
import { CurrentUserPermissionsProvider } from './CurrentUserPermissionsContext';

const logger = createScopedLogger('AuthContextProvider');

class AuthService implements IAuthService {
	private initializePromise?: Promise<void>;

	constructor(
		private msalInstance: PublicClientApplication,
		private config: Config,
	) {}

	async initialize(): Promise<void> {
		this.initializePromise ??= this.msalInstance.initialize().then(() => undefined);
		await this.initializePromise;
	}

	async signIn(preservePath?: string): Promise<void> {
		await this.initialize();

		// Store the current path for restoration after sign-in
		const pathToPreserve = preservePath || window.location.pathname + window.location.search;
		sessionStorage.setItem(PRE_AUTH_PATH_KEY, pathToPreserve);

		await this.msalInstance.loginRedirect({
			scopes: this.config.AUTH_SCOPE.split(' '),
			state: JSON.stringify({ returnPath: pathToPreserve }),
			extraQueryParameters: this.getB2CPolicyQueryParameters(),
		});
	}

	async signInWithProvider(provider: 'google' | 'facebook', preservePath?: string): Promise<void> {
		await this.initialize();

		const pathToPreserve = preservePath || window.location.pathname + window.location.search;
		sessionStorage.setItem(PRE_AUTH_PATH_KEY, pathToPreserve);

		await this.msalInstance.loginRedirect({
			scopes: this.config.AUTH_SCOPE.split(' '),
			state: JSON.stringify({ returnPath: pathToPreserve }),
			extraQueryParameters: {
				...this.getB2CPolicyQueryParameters(),
				domain_hint: provider === 'google' ? 'google.com' : 'facebook.com',
			},
		});
	}

	async signOut(): Promise<void> {
		await this.initialize();
		await this.msalInstance.logoutRedirect();
	}

	private async ensureAccessToken(): Promise<string | undefined> {
		await this.initialize();

		const accounts = this.msalInstance.getActiveAccount() ? [this.msalInstance.getActiveAccount()!] : this.msalInstance.getAllAccounts();
		if (accounts.length > 0) {
			const account = accounts[0];
			try {
				const r = await this.msalInstance.acquireTokenSilent({
					scopes: this.config.AUTH_SCOPE.split(' '),
					account: account,
				});
				return r.accessToken;
			} catch (error) {
				logger.error('Error acquiring token silently:', error);
			}
		}
		return undefined;
	}

	getAccessToken(_forceSignIn?: boolean): Promise<string | undefined> {
		return this.ensureAccessToken();
	}

	private getB2CPolicyQueryParameters(): Record<string, string> {
		const authority = new URL(this.config.AUTH_AUTHORITY);
		const pathSegments = authority.pathname.split('/').filter(Boolean);
		const policy = pathSegments[pathSegments.length - 1];
		if (!policy?.toLowerCase().startsWith('b2c_')) {
			return {};
		}

		return { p: policy };
	}
}

const AuthContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const config = useContext(ConfigContext);

	const { msalInstance, authService } = useMemo(() => {
		const msalConfig: Configuration = {
			auth: {
				clientId: config.AUTH_CLIENT_ID,
				authority: config.AUTH_AUTHORITY,
				knownAuthorities: config.AUTH_AUTHORITY_KNOWNS ? config.AUTH_AUTHORITY_KNOWNS.split(' ') : undefined,
				redirectUri: config.AUTH_REDIRECT_URI,
			},
			cache: {
				cacheLocation: 'localStorage', // Use localStorage to share auth state between tabs
				storeAuthStateInCookie: false,
			},
		};

		const msalInstance = new PublicClientApplication(msalConfig);
		const authService = new AuthService(msalInstance, config);

		return { msalInstance, authService };
	}, [config]);

	const [accounts, setAccounts] = useState<any[]>([]);
	const [isInitializing, setIsInitializing] = useState(true);

	// Create stable auth object using useMemo to prevent unnecessary re-renders
	const auth = useMemo<Auth>(
		() => ({
			isAuthenticated: accounts.length > 0,
			authService,
			account: accounts.length > 0 ? accounts[0] : null,
		}),
		[accounts, authService],
	);

	useEffect(() => {
		const load = () => {
			const newAccounts = msalInstance.getAllAccounts();
			if (!msalInstance.getActiveAccount() && newAccounts.length > 0) {
				msalInstance.setActiveAccount(newAccounts[0]);
			}
			// Only update state if account actually changed (assuming single account)
			setAccounts((prevAccounts) => {
				const prevUsername = prevAccounts[0]?.username;
				const newUsername = newAccounts[0]?.username;

				// If both empty or same username, no change
				if (prevUsername === newUsername) {
					return prevAccounts;
				}

				logger.debug('Auth state updated:', {
					isAuthenticated: newAccounts.length > 0,
					username: newUsername,
				});
				return newAccounts;
			});
		};

		// Initialize MSAL and handle redirect response
		const initializeMsal = async () => {
			try {
				logger.debug('Initializing MSAL instance...');
				// Initialize the MSAL instance before any API calls
				await authService.initialize();
				logger.debug('Instance initialized, starting handleRedirectPromise...');
				// This is critical - it processes the redirect response from Azure AD B2C
				const response = await msalInstance.handleRedirectPromise();
				if (response) {
					if (response.account) {
						msalInstance.setActiveAccount(response.account);
					}
					logger.debug('Authentication redirect handled successfully', {
						account: response.account?.username,
						scopes: response.scopes,
						tokenType: response.tokenType,
					});
				} else {
					logger.debug('No redirect response (normal page load)');
				}
				// Load auth state after handling redirect
				load();
			} catch (error) {
				logger.error('Error handling redirect promise:', error);
				// Still load the auth state even if there's an error
				load();
			} finally {
				setIsInitializing(false);
			}
		};

		initializeMsal();

		// Add event callback to handle auth state changes
		const callbackId = msalInstance.addEventCallback((event) => {
			logger.debug('MSAL event:', event.eventType);
			if (
				event.eventType === EventType.LOGIN_SUCCESS ||
				event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
				event.eventType === EventType.SSO_SILENT_SUCCESS ||
				event.eventType === EventType.HANDLE_REDIRECT_END
			) {
				if (
					(event.eventType === EventType.LOGIN_SUCCESS ||
						event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
						event.eventType === EventType.SSO_SILENT_SUCCESS) &&
					event.payload &&
					'account' in event.payload &&
					event.payload.account
				) {
					msalInstance.setActiveAccount(event.payload.account);
				}
				logger.debug('Reloading auth state due to event:', event.eventType);
				load();
			}
		});

		// Listen to storage events to sync auth state across browser tabs
		const handleStorageChange = (e: StorageEvent) => {
			// Check if MSAL-related keys have changed in localStorage
			if (e.key && e.key.startsWith('msal.')) {
				logger.debug('Auth state changed in another tab, reloading...');
				load();
			}
		};
		window.addEventListener('storage', handleStorageChange);

		// Use BroadcastChannel for better cross-tab communication (if supported)
		let authChannel: BroadcastChannel | null = null;
		if (typeof BroadcastChannel !== 'undefined') {
			try {
				authChannel = new BroadcastChannel('msal-auth-channel');
				authChannel.onmessage = (event) => {
					if (event.data === 'auth-state-changed') {
						logger.debug('Auth state changed notification via BroadcastChannel');
						load();
					}
				};
				// Notify other tabs when auth state changes in this tab
				const notifyOtherTabs = () => {
					authChannel?.postMessage('auth-state-changed');
				};
				// Broadcast on login/logout events
				msalInstance.addEventCallback((event) => {
					if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.LOGOUT_SUCCESS) {
						notifyOtherTabs();
					}
				});
			} catch (error) {
				logger.warn('BroadcastChannel not available:', error);
			}
		}

		// Cleanup: remove event listeners and callbacks on unmount
		return () => {
			if (callbackId) {
				msalInstance.removeEventCallback(callbackId);
			}
			window.removeEventListener('storage', handleStorageChange);
			if (authChannel) {
				authChannel.close();
			}
		};
	}, [msalInstance, authService]);

	return (
		<MsalProvider instance={msalInstance}>
			{isInitializing ? (
				<Spinner />
			) : (
				<AuthContext.Provider value={auth}>
					<CurrentUserPermissionsProvider>{children}</CurrentUserPermissionsProvider>
				</AuthContext.Provider>
			)}
		</MsalProvider>
	);
};

export default AuthContextProvider;
